import { styled } from '@linaria/react';
import { SegmentedControl, TextInput } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useCreateSlashCommand, useUpdateSlashCommand } from '../../../../api/agents-client/agent-client.hooks';
import type { SlashCommand } from '../../../../api/agents-client/agent-client.type';
import { formatPromptWithMentions } from '../../../../containers/flora/components/flora-mention/flora-mention.helpers';
import type { SelectedMention } from '../../../../containers/flora/components/flora-mention/flora-mention.type';
import { toast } from '../../../../containers/notifications';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { FloraPromptTextarea } from '../shared/flora-prompt-textarea';
import { inputStyles } from '../shared/input-styles';

type CommandScopeOption = 'organization' | 'personal';

interface AgentCommandPanelProps {
  isOpen: boolean;
  onClose: () => void;
  command?: SlashCommand | null;
  isAdmin: boolean;
  existingCommands: SlashCommand[];
  readOnly?: boolean;
}

export function AgentCommandPanel({
  isOpen,
  onClose,
  command,
  isAdmin,
  existingCommands,
  readOnly,
}: AgentCommandPanelProps) {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const createMutation = useCreateSlashCommand();
  const updateMutation = useUpdateSlashCommand();

  const isEditing = !!command;

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [promptMentions, setPromptMentions] = useState<SelectedMention[]>([]);
  const [scope, setScope] = useState<CommandScopeOption>('personal');

  useEffect(() => {
    if (isOpen) {
      setNameError(null);
      if (command) {
        setName(command.name);
        setDescription(command.description ?? '');
        setPrompt(command.prompt ?? '');
        setScope(command.organization && !command.user ? 'organization' : 'personal');
      } else {
        setName('');
        setDescription('');
        setPrompt('');
        setScope('personal');
      }
    }
  }, [isOpen, command]);

  const validateName = useCallback(
    (value: string): boolean => {
      const trimmed = value.trim();
      if (!trimmed) return false;
      if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(trimmed)) {
        setNameError('Use lowercase letters, numbers, and hyphens (e.g. my-command)');
        return false;
      }
      const isDuplicate = existingCommands.some(
        (cmd) => cmd.name.toLowerCase() === trimmed.toLowerCase() && cmd.id !== command?.id,
      );
      if (isDuplicate) {
        setNameError('A command with this name already exists');
        return false;
      }
      setNameError(null);
      return true;
    },
    [existingCommands, command?.id],
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (value.trim()) {
        validateName(value);
      } else {
        setNameError(null);
      }
    },
    [validateName],
  );

  const handleSave = useCallback(() => {
    if (!validateName(name)) return;
    if (!isEditing && scope === 'organization' && !organizationId) {
      toast.error('Organization context is unavailable. Please retry.');
      return;
    }

    const serializedPrompt = formatPromptWithMentions(prompt, promptMentions).trim() || null;

    if (isEditing && command) {
      updateMutation.mutate(
        {
          id: command.id,
          data: {
            name: name.trim(),
            description: description.trim() || null,
            prompt: serializedPrompt,
          },
        },
        {
          onSuccess: () => {
            toast.success('Command updated successfully');
            onClose();
          },
          onError: () => {
            toast.error('Failed to update command');
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          organization: scope === 'organization' ? (organizationId ?? null) : null,
          name: name.trim(),
          description: description.trim() || null,
          prompt: serializedPrompt,
        },
        {
          onSuccess: () => {
            toast.success('Command created successfully');
            onClose();
          },
          onError: () => {
            toast.error('Failed to create command');
          },
        },
      );
    }
  }, [
    name,
    description,
    prompt,
    promptMentions,
    scope,
    isEditing,
    command,
    organizationId,
    validateName,
    createMutation,
    updateMutation,
    onClose,
  ]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <Panel>
      <PanelHeader>
        <Text size="regular" weight="bold" color="var(--ink-darkest)">
          {readOnly ? 'View Command' : isEditing ? 'Edit Command' : 'New Command'}
        </Text>
        <CloseButton onClick={onClose} aria-label="Close panel">
          <Icon name="close" color="var(--ink-lighter)" size={18} style={{ cursor: 'pointer' }} />
        </CloseButton>
      </PanelHeader>

      <PanelBody>
        <FieldGroup>
          <Text size="small" weight="medium" color="var(--ink-dark)">
            Name
          </Text>
          <TextInput
            placeholder="my-command"
            value={name}
            onChange={(e) => handleNameChange(e.currentTarget.value)}
            error={nameError}
            readOnly={readOnly}
            styles={inputStyles}
          />
        </FieldGroup>

        <FieldGroup>
          <Text size="small" weight="medium" color="var(--ink-dark)">
            Description
          </Text>
          <TextInput
            placeholder="A short description of what this command does"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            readOnly={readOnly}
            styles={inputStyles}
          />
        </FieldGroup>

        <FieldGroup>
          <Text size="small" weight="medium" color="var(--ink-dark)">
            Prompt
          </Text>
          <FloraPromptTextarea
            key={command?.id ?? 'new'}
            value={prompt}
            onChange={setPrompt}
            onMentionsChange={setPromptMentions}
            placeholder="The prompt template for this command... Type @ to mention objects."
            minRows={6}
            maxRows={16}
            maxLength={80000}
            readOnly={readOnly}
          />
        </FieldGroup>

        {isAdmin && !isEditing && !readOnly && (
          <FieldGroup>
            <Text size="small" weight="medium" color="var(--ink-dark)">
              Scope
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(val) => setScope(val as CommandScopeOption)}
              data={[
                { label: 'Personal', value: 'personal' },
                { label: 'Organization', value: 'organization' },
              ]}
              fullWidth
              styles={{
                root: {
                  borderRadius: 8,
                  background: 'var(--sky-lighter)',
                },
                label: {
                  color: 'var(--ink-dark)',
                },
                indicator: {
                  background: 'var(--base-white)',
                  boxShadow: 'var(--box-shadow-small-soft)',
                },
              }}
            />
          </FieldGroup>
        )}
      </PanelBody>

      {!readOnly && (
        <PanelFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={isSaving}
            disabled={!name.trim() || !!nameError}
          >
            {isEditing ? 'Save Changes' : 'Create Command'}
          </Button>
        </PanelFooter>
      )}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--sky-lightest);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--sky-light);
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: var(--sky-lighter);
  }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PanelFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid var(--sky-light);
  flex-shrink: 0;
`;
